import { PrivateImage } from "@/components/PrivateMedia";
import { adminPhotoUrl } from "@/lib/adminNavigation";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/LanguageContext";
import { api, extractError } from "@/lib/api";

const STATUSES = ["pending", "reviewing", "resolved", "dismissed"];
const REASONS = [
  "harassment", "spam", "fake_profile", "scam",
  "inappropriate_content", "other",
];

const STATUS_BADGE_STYLES = {
  pending: { bg: "hsl(var(--nvl-accent) / 0.18)", fg: "hsl(var(--nvl-accent))" },
  reviewing: { bg: "hsl(var(--nvl-ink) / 0.08)", fg: "hsl(var(--nvl-ink))" },
  resolved: { bg: "hsl(140 40% 90%)", fg: "hsl(140 60% 25%)" },
  dismissed: { bg: "hsl(var(--muted))", fg: "hsl(var(--nvl-ink-soft))" },
};

export default function AdminModeration() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (status) params.status = status;
      if (reason) params.reason = reason;
      const r = await api.get("/admin/reports", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(extractError(e, t("app.admin.moderation.loadError")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, reason]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(lang === "it" ? "it-IT" : "en-GB");
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-moderation-page">
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {t("app.admin.nav.moderation")}
      </p>
      <h1 className="mt-2 text-3xl md:text-4xl">{t("app.admin.moderation.title")}</h1>
      <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {t("app.admin.moderation.subtitle")}
      </p>

      <div className="mt-6 flex flex-wrap gap-3 items-end">
        <div>
          <div className="text-xs uppercase tracking-widest mb-1"
               style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.filters.status")}
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="nvl-input"
            data-testid="admin-filter-status"
          >
            <option value="">{t("app.admin.moderation.filters.all")}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`app.admin.moderation.status.${s}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest mb-1"
               style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.filters.reason")}
          </div>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="nvl-input"
            data-testid="admin-filter-reason"
          >
            <option value="">{t("app.admin.moderation.filters.all")}</option>
            {REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`app.moderation.reasons.${r}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {total} · {t("app.admin.moderation.title")}
        </div>
      </div>

      <div
        className="mt-6 nvl-card overflow-hidden"
        style={{ borderColor: "hsl(var(--nvl-border))" }}
      >
        {loading ? (
          <div className="p-6 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}
               data-testid="admin-moderation-loading">
            {t("common.loading")}
          </div>
        ) : error ? (
          <div className="p-6 text-sm" style={{ color: "hsl(var(--destructive))" }}
               data-testid="admin-moderation-error">
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm" data-testid="admin-moderation-empty">
            {t("app.admin.moderation.empty")}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-moderation-table">
            <thead>
              <tr className="text-left" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                <th className="p-3">{t("app.admin.moderation.table.reported")}</th>
                <th className="p-3">{t("app.admin.moderation.table.reason")}</th>
                <th className="p-3">{t("app.admin.moderation.table.status")}</th>
                <th className="p-3">{t("app.admin.moderation.table.createdAt")}</th>
                <th className="p-3 text-right">{t("app.admin.moderation.table.action")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const s = STATUS_BADGE_STYLES[it.status] || STATUS_BADGE_STYLES.pending;
                return (
                  <tr key={it.id} className="border-t"
                      style={{ borderColor: "hsl(var(--nvl-border))" }}
                      data-testid={`admin-report-row-${it.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {it.reported?.profile?.main_photo_url ? (
                          <PrivateImage src={adminPhotoUrl(it.reported.profile.main_photo_url)}
                               alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full"
                               style={{ backgroundColor: "hsl(var(--muted))" }} />
                        )}
                        <div>
                          <div className="font-medium">
                            {it.reported?.profile?.display_name || it.reported?.email || "—"}
                          </div>
                          <div className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                            {it.reported?.account_status}
                            {it.reported?.moderation_status && it.reported.moderation_status !== "clean"
                              ? ` · ${it.reported.moderation_status}` : ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      {t(`app.moderation.reasons.${it.reason}`, {}, it.reason)}
                      {it.has_message_context ? (
                        <div className="text-[10px] uppercase tracking-widest mt-1"
                             style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                          {t("app.admin.moderation.table.hasMessage")}
                        </div>
                      ) : null}
                    </td>
                    <td className="p-3">
                      <span
                        className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest"
                        style={{ backgroundColor: s.bg, color: s.fg }}
                      >
                        {t(`app.admin.moderation.status.${it.status}`)}
                      </span>
                    </td>
                    <td className="p-3 text-xs">{fmt(it.created_at)}</td>
                    <td className="p-3 text-right">
                      <Link
                        to={`/admin/moderation/${it.id}`}
                        className="nvl-btn nvl-btn-outline text-xs"
                        data-testid={`admin-report-open-${it.id}`}
                      >
                        {t("app.admin.moderation.table.open")}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
